/* eslint-disable max-lines-per-function */

import { isEmpty, noop } from 'lodash';
import { useMemo } from 'react';

import { PatientConsentDocument, PatientConsentDocumentQuery } from '@dreryk/egabinet-patient-frontend-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { booleanFormatter, dateFormatter, PromiseVoidFunction } from '@dreryk/react-common';
import {
   createDynamicColumnsWidth,
   TableColumnConfiguration,
   Text,
   TextPlaceholder,
   ValueRendererProps,
} from '@dreryk/react-components';

import { patientConsentsAndAuthorizationsTranslationKeys } from '../../../translations';

import { RelatedVisitCell } from './cells/RelatedVisitCell';
import { ScanDocumentAttachmentLinksCell } from './cells/ScanDocumentAttachmentLinksCell';
import { ScanDocumentCell } from './cells/ScanDocumentCell';

const t = patientConsentsAndAuthorizationsTranslationKeys.consentsAndAuthorizations.table.columns;

type UseConsentsAndAuthorizationsDataConfigurationOptions = {
   patientId: number;
};

export const useConsentsAndAuthorizationsDataConfiguration = (
   options: UseConsentsAndAuthorizationsDataConfigurationOptions
): LazyLoadedDataConfigurationByCqrsQuery<PatientConsentDocument> => {
   return useLazyLoadedDataConfigurationByCqrsQuery<PatientConsentDocument>(() => {
      const query = new PatientConsentDocumentQuery();
      query.patientId = options.patientId;
      return query;
   });
};

type UseConsentsAndAuthorizationsTableColumnsOptions = {
   refetchData: PromiseVoidFunction;
   patientId: number;
};

export const useConsentsAndAuthorizationsTableColumns = (
   options: UseConsentsAndAuthorizationsTableColumnsOptions
): TableColumnConfiguration<PatientConsentDocument>[] => {
   return useMemo(() => {
      const columnsWidth = createDynamicColumnsWidth({
         name: '100%',
         creationDate: '142px',
         relatedVisit: '163px',
         attachmentsUploadDate: '149px',
         attachments: '205px',
         options: '24px',
      });

      return [
         {
            name: 'name',
            label: t.headers.name,
            width: columnsWidth.name,
            sortingDisabled: true,
            accessor: noop,

            renderer: ({ bean }: ValueRendererProps<PatientConsentDocument>) => (
               <Text testId={'name'} maxLines={3}>
                  {bean.name}
               </Text>
            ),
         },
         {
            name: 'creationDate',
            label: t.headers.creationDate,
            width: columnsWidth.creationDate,
            accessor: 'creationDate',
            formatter: dateFormatter,
         },
         {
            name: 'relatedVisit',
            label: t.headers.relatedVisit,
            width: columnsWidth.relatedVisit,
            sortingDisabled: true,
            accessor: noop,

            renderer: ({ bean }: ValueRendererProps<PatientConsentDocument>) => {
               if (!bean.createdOnVisitId) {
                  return <Text testId={'relatedVisit'}>{booleanFormatter(false)}</Text>;
               }

               return <RelatedVisitCell visitId={bean.createdOnVisitId} patientId={options.patientId} />;
            },
         },
         {
            name: 'attachmentsUploadDate',
            label: t.headers.attachmentsUploadDate,
            width: columnsWidth.attachmentsUploadDate,
            accessor: 'attachmentsUploadDate',
            formatter: dateFormatter,
            placeholder: TextPlaceholder.NO_DATA,
         },
         {
            name: 'attachments',
            label: t.headers.attachments,
            width: columnsWidth.attachments,
            accessor: 'attachments',
            sortingDisabled: true,

            renderer: ({ bean }: ValueRendererProps<PatientConsentDocument>) => {
               if (isEmpty(bean.attachments)) {
                  return (
                     <ScanDocumentCell
                        consentDocumentId={bean.id}
                        consentDocumentName={bean.name}
                        onConfirm={options.refetchData}
                     />
                  );
               }

               return (
                  <ScanDocumentAttachmentLinksCell attachments={bean.attachments!} consentDocumentName={bean.name} />
               );
            },
         },
         {
            name: 'options',
            width: columnsWidth.options,
            sortingDisabled: true,
            accessor: noop,
         },
      ];
   }, [options.refetchData, options.patientId]);
};
